import { useEffect } from 'react';

import { useProfilesData } from '@/features/user/api';

import { useAnalytics } from './use-analytics';

/**
 * Hook to track Google login success/failure after OAuth redirect
 *
 * This hook detects when a user returns from Google OAuth authentication
 * by checking for a localStorage flag set before redirect. After a short delay
 * to allow auth state to settle, it tracks the login result.
 *
 * @example
 * ```tsx
 * const AppRouter = () => {
 *   useGoogleLoginTracker();
 *   return <RouterProvider router={router} />;
 * };
 * ```
 */
export const useGoogleLoginTracker = () => {
  const { trackEvent, clearUserId } = useAnalytics();
  const { isSuccess: isAuthenticated } = useProfilesData();

  useEffect(() => {
    const MAX_ATTEMPT_AGE = 5 * 60 * 1000; // 5 minutes
    const AUTH_SETTLE_DELAY = 3000; // 3 seconds

    let attemptTimestamp: string | null = null;

    // Try to read from localStorage with error handling
    try {
      attemptTimestamp = localStorage.getItem('google_login_attempt');
    } catch {
      // Fail silently
    }

    // No attempt found - nothing to track
    if (!attemptTimestamp) {
      return () => {};
    }

    const attemptTime = Number.parseInt(attemptTimestamp, 10);
    const now = Date.now();
    const elapsed = now - attemptTime;

    // Attempt is too old - clear it and return
    if (elapsed >= MAX_ATTEMPT_AGE) {
      try {
        localStorage.removeItem('google_login_attempt');
      } catch {
        // Fail silently
      }
      return () => {};
    }

    // Wait for auth state to settle after redirect
    const timeoutId = setTimeout(() => {
      if (isAuthenticated) {
        trackEvent('loginApiSuccess', 1, { method: 'google' });
      } else {
        clearUserId();
        trackEvent('loginApiFailure', 1, {
          method: 'google',
          errorMessage: 'Google login failed or cancelled',
        });
      }

      // Clean up localStorage flag
      try {
        localStorage.removeItem('google_login_attempt');
      } catch {
        // Fail silently
      }
    }, AUTH_SETTLE_DELAY);

    // Cleanup timeout on unmount
    return () => {
      clearTimeout(timeoutId);
    };
  }, [isAuthenticated, trackEvent, clearUserId]);
};
